import { LinksFunction, LoaderArgs, json } from '@shopify/remix-oxygen';
import {
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  type ShouldRevalidateFunction,
} from '@remix-run/react';
import styles from '~/styles/app.css';
import styled, {
  ThemeProvider,
  StyleSheetManager,
  keyframes,
} from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { Theme, globalTheme } from './theme';
import { RecoilRoot } from 'recoil';
import InitialLoader from './components/InitialLoader';
import Layout from './layout';
import { MetaTags } from './components/MetaTags';
import { createHead } from 'remix-island';
import { GoogleTagManagerHead } from './components/GoogleTagManager';
import { useGTMConsent } from './common/hooks/useGTMConsent';
import { useKlaviyoConsent } from './common/hooks/useKlaviyoConsent';
import { useKlaviyoPageTracking } from './common/hooks/useKlaviyoPageTracking';
import { SEO, FALLBACK_SITE_URL } from './common/constants/seo';
import { colors } from './theme/colors';
import { getRegionInfoFromRequest } from './lib/geolocation.server';
import { getRegionInfoFromEnv, regionInfoState } from '~/recoil/useRegion';
import { isFlagEnabledDefaultOn } from '~/lib/featureFlags';
import { REGIONS, STORES, type Region } from '~/common/constants/region';
import { resolveRegionInfo } from '~/common/utils/region';
import { CAMPAIGN_PATHS } from '~/common/constants/gatedRoutes';
import { splitDescription } from '~/lib/signUpCopy.server';

export async function loader({
  request,
  context: { env, storefront, euEarlyAccess },
}: LoaderArgs) {
  const { region, countryCode } = await getRegionInfoFromRequest(
    request,
    storefront,
    {
      skipCache: env.DISABLE_LOCALIZATION_CACHE === 'true',
    },
  );

  return json({
    ENV: {
      API_URL: env.PUBLIC_API_URL,
      PUBLIC_SALE_TITLE: env.PUBLIC_SALE_TITLE,
      PUBLIC_MYSTERY_COLLECTION_NAME: env.PUBLIC_MYSTERY_COLLECTION_NAME,
      PUBLIC_MYSTERY_COLLECTION_GROUPS: env.PUBLIC_MYSTERY_COLLECTION_GROUPS,
      PASSWORD: env.PASSWORD,
      PUBLIC_SHOW_PRODUCTS: env.PUBLIC_SHOW_PRODUCTS,
      PUBLIC_ENABLE_FLASH_SALE: env.PUBLIC_ENABLE_FLASH_SALE,
      PUBLIC_ENABLE_FLOATING_FLASH_SALE: env.PUBLIC_ENABLE_FLOATING_FLASH_SALE,
      PUBLIC_SHOW_SIGNUP_PAGE: env.PUBLIC_SHOW_SIGNUP_PAGE,
      // Public by nature — it is the path segment of the onsite script URL. The
      // private Klaviyo key must never appear in this object.
      PUBLIC_KLAVIYO_COMPANY_ID: env.PUBLIC_KLAVIYO_COMPANY_ID ?? '',
      REGIONS_ENABLED: isFlagEnabledDefaultOn(env.PUBLIC_ENABLE_REGIONS),
      EU_EARLY_ACCESS: euEarlyAccess,
      ENABLE_SWEATER_SIZES: env.ENABLE_SWEATER_SIZES,
      ENABLE_TANK_TOP_SIZES: env.ENABLE_TANK_TOP_SIZES,
      ENABLE_CREW_NECK_SIZES: env.ENABLE_CREW_NECK_SIZES,
      PUBLIC_SITE_URL: env.PUBLIC_SITE_URL ?? '',
      RESOLVED_REGION: region,
      RESOLVED_COUNTRY_CODE: countryCode,
    },
    rollingHeaderMessages: getRollingHeaderMessages(
      env,
      ROLLING_HEADER_MARKET_KEYS[
        resolveRegionInfo(storefront.i18n.country).region
      ] ??
        (countryCode === STORES.ROW.countryCode
          ? 'ROLLING_HEADER_MESSAGES_ROW'
          : null),
    ),
  });
}

type RollingHeaderKey =
  | 'ROLLING_HEADER_MESSAGES_EU'
  | 'ROLLING_HEADER_MESSAGES_US'
  | 'ROLLING_HEADER_MESSAGES_ROW';

/** Markets with their own list; the UK market has none (base list, or RoW's). */
const ROLLING_HEADER_MARKET_KEYS: Partial<Record<Region, RollingHeaderKey>> = {
  [REGIONS.EU]: 'ROLLING_HEADER_MESSAGES_EU',
  [REGIONS.US]: 'ROLLING_HEADER_MESSAGES_US',
};

/**
 * Per-market copy (prices and thresholds differ): `_EU` / `_US` for those markets, `_ROW` for
 * the Rest of World store (which prices in the UK market, so it is keyed on the store),
 * else, or when the own list is unset, the base (UK) list. Keyed on the market prices are shown in,
 * not the geolocated region, so an EU visitor
 * still shopping in GBP is told the GBP threshold. Split here, so neither the raw var
 * nor the separator reaches the client.
 */
const getRollingHeaderMessages = (
  env: Env,
  ownKey: RollingHeaderKey | null,
) => {
  const own = ownKey ? splitDescription(ownKey, env[ownKey]) : [];
  return own.length
    ? own
    : splitDescription('ROLLING_HEADER_MESSAGES', env.ROLLING_HEADER_MESSAGES);
};

/**
 * `EU_EARLY_ACCESS` changes when the EU launch cookie is minted. The password form is
 * an action, so it revalidates anyway; the sign-up page unlocks over `fetch` and then
 * navigates client-side, which would leave root stale until a reload — no EU pricing
 * shown, no picker. ponytail: re-runs root on every launch-page navigation (one
 * CacheLong query), narrow it if that ever shows up.
 */
export const shouldRevalidate: ShouldRevalidateFunction = ({
  nextUrl,
  defaultShouldRevalidate,
}) =>
  defaultShouldRevalidate ||
  nextUrl.pathname.startsWith(CAMPAIGN_PATHS.euLaunch.gate);

export const meta = ({ data }: { data: any }) => {
  const siteUrl = data?.ENV?.PUBLIC_SITE_URL || FALLBACK_SITE_URL;

  return [
    { title: SEO.defaultTitle },
    { name: 'description', content: SEO.defaultDescription },
    { name: 'theme-color', content: colors.colorMain },
    { property: 'og:url', content: siteUrl },
    { property: 'og:title', content: SEO.defaultTitle },
    { property: 'og:description', content: SEO.defaultDescription },
    { property: 'og:image', content: SEO.defaultOgImage },
    { property: 'og:image:width', content: String(SEO.defaultOgImageWidth) },
    { property: 'og:image:height', content: String(SEO.defaultOgImageHeight) },
    { property: 'og:image:alt', content: SEO.defaultOgImageAlt },
    { property: 'og:site_name', content: SEO.brandName },
    { name: 'twitter:card', content: SEO.twitterCard },
    { name: 'twitter:title', content: SEO.defaultTitle },
    { name: 'twitter:description', content: SEO.defaultDescription },
    { name: 'twitter:image', content: SEO.defaultOgImage },
  ];
};

export const links: LinksFunction = () => {
  return [
    { rel: 'stylesheet', href: styles },
    { rel: 'manifest', href: '/manifest.webmanifest' },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      href: '/favicon-16x16.png?v=3',
      // href: '/favicon-light-16.png?v=3',
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      href: '/favicon-32x32.png?v=3',
      // href: '/favicon-light-32.png?v=3',
    },
    // {
    //   rel: 'apple-touch-icon',
    //   sizes: '180x180',
    //   href: '/favicon-light-180.png?v=3',
    // },
    // {
    //   rel: 'icon',
    //   type: 'image/png',
    //   sizes: '192x192',
    //   href: '/favicon-light-192.png?v=3',
    // },
    // {
    //   rel: 'icon',
    //   type: 'image/png',
    //   sizes: '512x512',
    //   href: '/favicon-light-512.png?v=3',
    // },
    {
      rel: 'preload',
      href: '/assets/icons/header-logo.svg',
      as: 'image',
      type: 'image/svg+xml',
    },
    {
      rel: 'preconnect',
      href: 'https://cdn.shopify.com',
    },
    {
      rel: 'preconnect',
      href: 'https://shop.app',
    },
    {
      rel: 'preload',
      href: '/fonts/PantonRustHeavy/PantonRustHeavy-Base.woff',
      as: 'font',
      type: 'font/woff',
      crossOrigin: 'anonymous',
    },
    {
      rel: 'preload',
      href: '/fonts/PantonRustHeavy/PantonRustHeavy-Base.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    },
    {
      rel: 'preload',
      href: '/fonts/CircularStd/CircularStd-Book.woff2',
      as: 'font',
      type: 'font/woff2',
      crossOrigin: 'anonymous',
    },
  ];
};

export const Head = createHead(() => (
  <>
    <meta charSet="utf-8" />
    <meta
      name="viewport"
      content="width=device-width,initial-scale=1,viewport-fit=cover"
    />
    <MetaTags />
    <Meta />
    <Links />
    <GoogleTagManagerHead />
  </>
));

export default function App() {
  const data = useLoaderData<typeof loader>();
  const navigation = useNavigation();
  const isNavigating = navigation.state !== 'idle';

  function shouldForwardProp(propName: any, target: any) {
    if (typeof target === 'string') {
      return isPropValid(propName);
    }
    return true;
  }

  useGTMConsent();
  // From loader data rather than `window.ENV`: that global is assigned by an inline
  // script further down the body, and a hook should not depend on that ordering.
  useKlaviyoConsent(data.ENV.PUBLIC_KLAVIYO_COMPANY_ID);
  // Separate from the hook above: that one owns the script lifecycle, this one owns
  // navigation. Needs no company id — consent and delivery are handled downstream.
  useKlaviyoPageTracking();

  return (
    <>
      <Head />
      <RecoilRoot
        initializeState={({ set }) => {
          set(regionInfoState, getRegionInfoFromEnv(data.ENV));
        }}
      >
        <StyleSheetManager
          enableVendorPrefixes
          shouldForwardProp={shouldForwardProp}
        >
          <ThemeProvider theme={globalTheme as Theme}>
            {isNavigating && <NavigationProgress />}
            <InitialLoader />
            <Layout>
              <Outlet />
            </Layout>
            <ScrollRestoration />
            <script
              dangerouslySetInnerHTML={{
                __html: `window.ENV = ${JSON.stringify(data.ENV)}`,
              }}
            />
            <Scripts />
            <LiveReload />
          </ThemeProvider>
        </StyleSheetManager>
      </RecoilRoot>
    </>
  );
}

const progressAnimation = keyframes`
  0% { transform: scaleX(0); transform-origin: left; }
  50% { transform: scaleX(0.7); transform-origin: left; }
  100% { transform: scaleX(1); transform-origin: left; }
`;

const NavigationProgress = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: ${({ theme }) => theme.colors.white};
  z-index: ${({ theme }) => theme.zIndex.loader};
  animation: ${progressAnimation} 1.5s ease-in-out infinite;
`;
